"use client";
import React from "react";

import {
  Box,
  BoxDiv,
  BoxSx,
  FontSize,
  Icon,
  IconNameType,
  IconProps,
  ThemeSpaces
} from "../../foundations";
import { Text } from "../../foundations/Typography/Text";
import { useId } from "../../hooks/useId";
import { useWarning } from "../../hooks/useWarning";

type AvatarSize = "xs" | "sm" | "md" | "lg";

type AvatarProps = {
  size?: AvatarSize;
  outline?: boolean;
  icon?: IconNameType;
  src?: string;
  alt?: string;
  children?: string;
} & BoxDiv;

const sizeMap: Record<
  AvatarSize,
  {
    container: BoxSx;
    iconSize: IconProps["size"];
    padding: ThemeSpaces;
    fontSize: keyof typeof FontSize;
  }
> = {
  xs: {
    container: { width: "spacing-7", height: "spacing-7" },
    iconSize: "xxs",
    padding: "spacing-2",
    fontSize: "font-size-2"
  },
  sm: {
    container: { width: "spacing-8", height: "spacing-8" },
    iconSize: "xs",
    // @ts-ignore
    padding: "6px",
    fontSize: "font-size-3"
  },
  md: {
    container: { width: "spacing-9", height: "spacing-9" },
    iconSize: "sm",
    padding: "spacing-3",
    fontSize: "font-size-4"
  },
  lg: {
    container: { width: "spacing-10", height: "spacing-10" },
    iconSize: "md",
    // @ts-ignore
    padding: "10px",
    fontSize: "font-size-6"
  }
};

export const Avatar: React.FC<AvatarProps> = ({
  size = "md",
  outline = false,
  icon,
  src,
  alt,
  children,
  ...props
}) => {
  const { container, iconSize, padding, fontSize } = sizeMap[size];
  const { warn } = useWarning();
  const id = useId();

  if (src && !alt) {
    warn(
      "An alt property should be passed when using an image as Avatar content."
    );
  }

  const role = !children ? "img" : undefined;
  const ariaLabel = !children ? alt || "User avatar" : undefined;
  const avatarIcon = icon ? icon : !children && !src ? "user" : undefined;

  const photoStyles = React.useMemo<BoxSx>(
    () => ({
      backgroundImage: `url(${src})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat"
    }),
    [src]
  );

  const outlineStyles = React.useMemo<BoxSx>(
    () => ({
      outline: "1px solid",
      outlineColor: "stroke-neutral-default"
    }),
    []
  );

  const avatarStyles = React.useMemo<BoxSx>(
    () => ({
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexShrink: 0,
      overflow: "hidden",
      whiteSpace: "nowrap",
      borderRadius: "radius-round",
      ...container,
      ...(outline && { ...outlineStyles }),
      ...(src
        ? { ...photoStyles }
        : {
            textAlign: "center",
            backgroundColor: "surface-neutral-tertiary",
            p: padding
          }),
      ":focus-visible": {
        outline: "2px solid",
        outlineColor: "focus",
        outlineOffset: "2px"
      }
    }),
    [container, outline, outlineStyles, src, photoStyles, padding]
  );

  return (
    <Box
      id={props.id || id}
      role={role}
      aria-label={ariaLabel}
      tabIndex={0}
      sx={{ ...avatarStyles }}
    >
      {!children && !src && (
        <Icon
          icon={avatarIcon as IconNameType}
          size={iconSize}
          color="control-enabled"
          sx={{
            display: "flex",
            alignItems: "center"
          }}
        />
      )}
      {!icon && !!children && (
        <Text
          variant="body-2-medium"
          size={fontSize}
          sx={{ color: "text-secondary" }}
        >
          {children}
        </Text>
      )}
    </Box>
  );
};
